// Top-center stepper showing the retro phase pills. Click a pill to jump
// to that phase (facilitator only). Non-facilitators see the same pills
// but they're inert — phase advances arrive via CRDT sync.
//
// The `archived` phase is hidden from the stepper; getting to/from archived
// is a separate menu action (facilitator's "End retro" / "Reopen retro").

import type * as Y from 'yjs';
import type { RetroPhase } from '@frame/shared';
import { ACTIVE_PHASES, PHASE_LABELS } from '@frame/shared';
import { setPhase } from '../binding/metaMap';

interface Props {
  doc: Y.Doc;
  phase: RetroPhase | undefined;
  isFacilitator: boolean;
}

export function PhaseBar({ doc, phase, isFacilitator }: Props) {
  const current = phase ?? 'lobby';
  const currentIndex = ACTIVE_PHASES.indexOf(current);

  return (
    <div
      role="group"
      aria-label="Retro phase"
      className="
        inline-flex items-center gap-0.5 p-1
        bg-bg-panel border border-border-default rounded-3 shadow-sm
      "
    >
      {ACTIVE_PHASES.map((p, i) => {
        const isCurrent = p === current;
        const isPast = i < currentIndex;
        return (
          <button
            key={p}
            type="button"
            aria-current={isCurrent ? 'step' : undefined}
            disabled={!isFacilitator}
            onClick={() => isFacilitator && setPhase(doc, p)}
            title={isFacilitator ? `Jump to ${PHASE_LABELS[p]}` : PHASE_LABELS[p]}
            className={[
              'h-7 px-3 inline-flex items-center justify-center rounded-2',
              'text-12 font-medium tabular-nums',
              'transition-colors duration-micro ease-out',
              'focus-visible:outline-none focus-visible:shadow-focus',
              isCurrent
                ? 'bg-bg-active text-fg-primary'
                : isPast
                  ? 'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary'
                  : 'text-fg-tertiary hover:bg-bg-hover hover:text-fg-secondary',
              !isFacilitator && 'cursor-default hover:bg-transparent',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="mr-1.5 text-fg-tertiary tabular-nums">{i + 1}</span>
            {PHASE_LABELS[p]}
          </button>
        );
      })}
    </div>
  );
}
